import { LoggerImpl, type Logger, type LoggingConfig, log } from "./logger";

declare global {
  interface Window {
    __CHECKOUT_WEBAPP_VERSION__: string;
    sessionConfig?: string;
    logger?: Logger;
  }
}

/**
 * Initialize the logger in the background without blocking app startup
 * @returns Promise that resolves when logger is initialized
 */
export function initializeLoggerInBackground(): Promise<Logger> {
  return new Promise((resolve, reject) => {
    // Use requestIdleCallback for better performance, fallback to setTimeout
    const scheduleInit = (callback: () => void) => {
      if ("requestIdleCallback" in window) {
        requestIdleCallback(callback, { timeout: 1000 });
      } else {
        setTimeout(callback, 1);
      }
    };

    scheduleInit(() => {
      try {
        if (!window.sessionConfig) {
          reject(new Error("Session config not found"));
          return;
        }

        // Try to parse sessionConfig
        const decodedSessionConfig = JSON.parse(atob(window.sessionConfig));
        if (!decodedSessionConfig || typeof decodedSessionConfig !== "object") {
          reject(new Error("Session config is not a valid object"));
          return;
        }

        // Get the global logger instance and initialize it
        const logger = window.logger;
        if (!logger || !(logger instanceof LoggerImpl)) {
          reject(new Error("Logger instance not found"));
          return;
        }

        // Transform snake_case JSON to camelCase TypeScript interface
        const rawLoggingConfig = decodedSessionConfig.logging;

        // Default values
        const DEFAULT_BUFFER_MAX_SIZE = 1024;
        const DEFAULT_BUFFER_MAX_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

        const rawBufferMaxSize =
          rawLoggingConfig.dashcam_mode?.buffer_max_size ??
          rawLoggingConfig.dashcamMode?.bufferMaxSize ??
          0;
        const rawBufferMaxDuration =
          rawLoggingConfig.dashcam_mode?.buffer_max_duration ??
          rawLoggingConfig.dashcamMode?.bufferMaxDuration ??
          0;

        const transformedConfig: LoggingConfig = {
          enabled: rawLoggingConfig.enabled ?? true,
          dashcamMode: {
            enabled:
              rawLoggingConfig.dashcam_mode?.enabled ??
              rawLoggingConfig.dashcamMode?.enabled ??
              false,
            bufferMaxDuration:
              rawBufferMaxDuration > 0
                ? rawBufferMaxDuration
                : DEFAULT_BUFFER_MAX_DURATION,
            bufferMaxSize:
              rawBufferMaxSize > 0 ? rawBufferMaxSize : DEFAULT_BUFFER_MAX_SIZE,
          },
          matchers: (rawLoggingConfig.matchers || []).map(
            (matcher: {
              level?: string;
              package?: string;
              context?: Record<string, string> | null;
            }) => ({
              level: matcher.level ? matcher.level.toLowerCase() : undefined,
              package: matcher.package,
              context: matcher.context
                ? new Map<string, string>(
                    Object.entries(matcher.context).map(([k, v]) => [
                      k,
                      String(v),
                    ]),
                  )
                : undefined,
            }),
          ),
        };

        // Initialize with config, token, and sessionId
        logger.initialize(
          {
            version: window.__CHECKOUT_WEBAPP_VERSION__,
          },
          transformedConfig,
          decodedSessionConfig.token,
          decodedSessionConfig.session_id,
        );

        log("info", "logger", "Logger initialized successfully in background");
        resolve(logger);
      } catch (error) {
        log(
          "error",
          "logger",
          "Failed to initialize logger",
          error instanceof Error ? error : new Error(String(error)),
        );
        reject(error);
      }
    });
  });
}
